import type { IteroFullRx, IteroPrep } from '../../../../services/iteroBrowse';

const SECONDS_PER_MINUTE = 60;
const UNKNOWN_SCANNER_MODEL_ID = -1;
const UNKNOWN_TEXT = /^unknown$/i;

export const UNNAMED_PATIENT = 'Unnamed patient';

function clean(value: string | null | undefined): string {
    return value?.trim() ?? '';
}

export function formatPatientName(lastName: string | null, firstName: string | null): string {
    const parts = [clean(lastName), clean(firstName)].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : UNNAMED_PATIENT;
}

export function formatFullName(firstName: string | null, lastName: string | null): string {
    const parts = [clean(firstName), clean(lastName)].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : UNNAMED_PATIENT;
}

// Plain words rather than iTero's mm:ss, which reads like a clock time out of context.
export function formatScanDuration(seconds: number | null): string | null {
    if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return null;
    const whole = Math.round(seconds);
    const minutes = Math.floor(whole / SECONDS_PER_MINUTE);
    const rest = whole % SECONDS_PER_MINUTE;
    if (minutes === 0) return `${rest} s`;
    return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}

export function scannerModelLabel(id: number | null, description: string | null): string | null {
    const text = clean(description);
    if (id === UNKNOWN_SCANNER_MODEL_ID || !text || UNKNOWN_TEXT.test(text)) return null;
    return text;
}

export function prepLabel(prep: IteroPrep): string {
    const tooth = prep.adaId === null ? '' : `#${prep.adaId}`;
    const unit = clean(prep.unitTypeName);
    return [tooth, unit].filter(Boolean).join(' ') || 'Unspecified tooth';
}

export function genderLabel(gender: string | null | undefined): string | null {
    const text = clean(gender);
    if (!text) return null;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function doctorLabel(name: string | null, license: string | null): string | null {
    const doctor = clean(name);
    const licence = clean(license);
    if (!doctor) return licence ? `License ${licence}` : null;
    return licence ? `${doctor} (license ${licence})` : doctor;
}

export function procedureLabel(name: string | null, typeName: string | null): string | null {
    const parts = [clean(name), clean(typeName)].filter(Boolean);
    return parts.length > 0 ? parts.join(' / ') : null;
}

export interface DetailRow {
    label: string;
    value: string | null;
    copyable?: boolean;
    multiline?: boolean;
}

export interface DetailFormatters {
    dateTime: (value: string | null) => string;
    dateOnly: (value: string | null) => string;
    date: (value: string | null) => string;
    time: (value: string | null) => string;
    shortDate: (value: string | null) => string;
}

export const NO_LAB = 'No lab';
export const NEVER_SCANNED = 'Never';

export function orEmpty(value: string | null | undefined): string | null {
    const text = value?.trim();
    return text ? text : null;
}

export function countNoun(count: number, singular: string, plural: string): string {
    return `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`;
}

const numberText = (value: number | null | undefined): string | null =>
    typeof value === 'number' && Number.isFinite(value) ? String(value) : null;

export function rxSummaryRows(rx: IteroFullRx, format: DetailFormatters): DetailRow[] {
    const patient = rx.patient;
    const order = rx.order;
    const patientName = patient
        ? formatFullName(patient.firstName ?? null, patient.lastName ?? null)
        : null;
    return [
        { label: 'Rx version', value: numberText(rx.version) },
        { label: 'Rx ID', value: orEmpty(rx.id), copyable: true },
        { label: 'Patient', value: patientName },
        { label: 'Date of birth', value: orEmpty(format.dateOnly(patient?.dateOfBirth ?? null)) },
        { label: 'Gender', value: genderLabel(patient?.gender) },
        {
            label: 'Doctor',
            value: doctorLabel(rx.doctor?.name ?? null, rx.doctor?.licence ?? null)
        },
        { label: 'Procedure ID', value: numberText(order?.procedureId) },
        { label: 'Procedure type ID', value: numberText(order?.procedureTypeId) },
        { label: 'Procedure map ID', value: numberText(order?.procedureMapId) },
        { label: 'Case type ID', value: numberText(order?.caseTypeId) }
    ];
}

const JSON_INDENT = 2;

export function rxJson(raw: Record<string, unknown>): string {
    return JSON.stringify(raw, null, JSON_INDENT);
}
